import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  LocationOn as LocationIcon,
  Map as MapIcon,
  CalendarToday as CalendarIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  Today as TodayIcon,
} from "@mui/icons-material";
import requestedAreasApi from "../../api/requestedAreas";
import Layout from "../../components/layout/Layout";

interface RequestedArea {
  _id: string;
  longitude: number;
  latitude: number;
  address: string;
  userId?: {
    _id: string;
    name: string;
    phoneNumber: string;
    email: string;
  };
  createdAt: string;
}

interface Stats {
  total: number;
  today: number;
  thisWeek: number;
}

const RequestedAreas: React.FC = () => {
  const [requestedAreas, setRequestedAreas] = useState<RequestedArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<Stats | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [cityFilter, setCityFilter] = useState<string>("");
  const [cities, setCities] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Dialog state
  const [selectedArea, setSelectedArea] = useState<RequestedArea | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState<RequestedArea | null>(null);

  // Handle search input with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchRequestedAreas();
    fetchStats();
  }, [page, rowsPerPage, debouncedSearch, cityFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchRequestedAreas = async () => {
    setLoading(true);
    try {
      const data = await requestedAreasApi.getRequestedAreas({
        search: debouncedSearch,
        city: cityFilter,
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        sortOrder,
      });

      setRequestedAreas(data.requestedAreas);
      setTotal(data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.message || "Error fetching requested areas");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await requestedAreasApi.getRequestedAreasStats();
      setStats(data.stats);
    } catch (err: any) {
      console.error("Error fetching stats:", err);
    }
  };

  const fetchCities = async () => {
    try {
      const data = await requestedAreasApi.getRequestedAreasCities();
      setCities(data.cities);
    } catch (err: any) {
      console.error("Error fetching cities:", err);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setCityFilter("");
    setSortBy("createdAt");
    setSortOrder("desc");
  };

  const handleViewArea = (area: RequestedArea) => {
    setSelectedArea(area);
    setViewDialogOpen(true);
  };

  const handleDeleteArea = (area: RequestedArea) => {
    setAreaToDelete(area);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!areaToDelete) return;

    try {
      await requestedAreasApi.deleteRequestedArea(areaToDelete._id);
      setRequestedAreas(requestedAreas.filter(area => area._id !== areaToDelete._id));
      setDeleteDialogOpen(false);
      setAreaToDelete(null);
      fetchStats(); // Refresh stats
    } catch (err: any) {
      setError(err.response?.data?.message || "Error deleting requested area");
    }
  };

  const getGoogleMapsUrl = (latitude: number, longitude: number) => {
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  return (
    <Layout>
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Requested Areas
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchRequestedAreas(); fetchStats(); }}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Stats Cards */}
        {stats && (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2, mb: 3 }}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
                <LocationIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary">
                  {stats.total}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Requests
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
                <TodayIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" color="success.main">
                  {stats.today}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Today
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
                <TrendingUpIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" color="warning.main">
                  {stats.thisWeek}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                This Week
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2 }}>
            <TextField
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              size="small"
              placeholder="Search by address, user name, or phone number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Sort By"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="createdAt">Date Created</MenuItem>
              <MenuItem value="address">Address</MenuItem>
            </TextField>
            <TextField
              select
              label="Sort Order"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
              fullWidth
              size="small"
            >
              <MenuItem value="desc">Newest First</MenuItem>
              <MenuItem value="asc">Oldest First</MenuItem>
            </TextField>
            <TextField
              select
              label="Filter by City"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">All Cities</MenuItem>
              {cities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                startIcon={<ClearIcon />}
                fullWidth
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Requested Areas Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
            <CircularProgress />
          </Box>
        ) : requestedAreas.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <LocationIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No requested areas found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search criteria or filters
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Address</TableCell>
                  <TableCell>Coordinates</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Requested Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requestedAreas.map((area) => (
                  <TableRow key={area._id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {area.address || "No address provided"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {area.latitude?.toFixed(6)}, {area.longitude?.toFixed(6)}
                        </Typography>
                        {area.latitude && area.longitude && (
                          <Tooltip title="Open in Google Maps">
                            <IconButton
                              size="small"
                              onClick={() => window.open(getGoogleMapsUrl(area.latitude, area.longitude), '_blank')}
                            >
                              <MapIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {area.userId ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {area.userId.name || "Unknown"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {area.userId.phoneNumber}
                          </Typography>
                        </Box>
                      ) : (
                        <Chip label="Anonymous" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {formatDate(area.createdAt)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewArea(area)}
                            color="primary"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteArea(area)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}



        {/* View Area Dialog */}
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LocationIcon color="primary" />
              Area Details
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedArea && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>Location Information</Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Address:</strong> {selectedArea.address || "No address provided"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Latitude:</strong> {selectedArea.latitude?.toFixed(6)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Longitude:</strong> {selectedArea.longitude?.toFixed(6)}
                    </Typography>
                    {selectedArea.latitude && selectedArea.longitude && (
                      <Button
                        variant="outlined"
                        startIcon={<MapIcon />}
                        onClick={() => window.open(getGoogleMapsUrl(selectedArea.latitude, selectedArea.longitude), '_blank')}
                        sx={{ mt: 2 }}
                      >
                        Open in Google Maps
                      </Button>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="h6" gutterBottom>User Information</Typography>
                    {selectedArea.userId ? (
                      <>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Name:</strong> {selectedArea.userId.name || "Unknown"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Phone:</strong> {selectedArea.userId.phoneNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Email:</strong> {selectedArea.userId.email || "Not provided"}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Anonymous request
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      <strong>Requested:</strong> {formatDate(selectedArea.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this requested area? This action cannot be undone.
            </Typography>
            {areaToDelete && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Address: {areaToDelete.address || "No address provided"}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default RequestedAreas; 